// src/utils/sitemapGenerator.ts
export interface SitemapUrl {
  loc: string;
  lastmod?: string;
  changefreq?: 'always' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'yearly' | 'never';
  priority?: number;
}

export interface SitemapData {
  originals: Array<{
    id: string;
    slug: string;
    title: string;
    updated_at: string;
    is_published: boolean;
  }>;
  cinema: Array<{
    id: string;
    slug: string;
    title: string;
    updated_at: string;
    is_published: boolean;
  }>;
  podcasts: Array<{
    id: string;
    slug: string;
    title: string;
    updated_at: string;
    is_published: boolean;
  }>;
  animations: Array<{
    id: string;
    slug: string;
    title: string;
    updated_at: string;
    is_published: boolean;
  }>;
}

class SitemapGenerator {
  private baseUrl = import.meta.env.VITE_NODE_ENV === 'production' 
    ? 'https://strymhub.com' 
    : 'http://localhost:5173';
  
  generateMainSitemap(): string {
    const staticPages: SitemapUrl[] = [
      {
        loc: this.baseUrl,
        changefreq: 'daily',
        priority: 1.0,
        lastmod: new Date().toISOString().split('T')[0]
      },
      {
        loc: `${this.baseUrl}/originals`,
        changefreq: 'daily',
        priority: 0.9,
      },
      {
        loc: `${this.baseUrl}/cinema`,
        changefreq: 'daily',
        priority: 0.9,
      },
      {
        loc: `${this.baseUrl}/media`,
        changefreq: 'daily',
        priority: 0.9,
      },
      {
        loc: `${this.baseUrl}/podcasts`,
        changefreq: 'daily',
        priority: 0.9,
      },
      {
        loc: `${this.baseUrl}/animations`,
        changefreq: 'daily',
        priority: 0.9,
      },
      {
        loc: `${this.baseUrl}/sneak-peeks`,
        changefreq: 'weekly',
        priority: 0.8,
      },
      {
        loc: `${this.baseUrl}/about`,
        changefreq: 'monthly',
        priority: 0.7,
      },
      {
        loc: `${this.baseUrl}/contact`,
        changefreq: 'monthly',
        priority: 0.6,
      },
      {
        loc: `${this.baseUrl}/privacy-policy`,
        changefreq: 'yearly',
        priority: 0.5,
      },
      {
        loc: `${this.baseUrl}/terms-of-service`,
        changefreq: 'yearly',
        priority: 0.5,
      }
    ];

    return this.generateXmlSitemap(staticPages);
  }

  generateContentSitemap(content: SitemapData['originals'], contentType: 'originals' | 'cinema' | 'podcasts' | 'animations'): string {
    const urls: SitemapUrl[] = content
      .filter(item => item.is_published)
      .map(item => ({
        loc: `${this.baseUrl}/${contentType}/${item.slug}`,
        lastmod: new Date(item.updated_at).toISOString().split('T')[0],
        changefreq: 'weekly' as const,
        priority: 0.8
      }));

    return this.generateXmlSitemap(urls);
  }

  generateSitemapIndex(): string {
    const sitemaps = [
      `${this.baseUrl}/sitemap.xml`,
      `${this.baseUrl}/sitemap-originals.xml`,
      `${this.baseUrl}/sitemap-media.xml`,
      `${this.baseUrl}/sitemap-podcasts.xml`,
      `${this.baseUrl}/sitemap-animations.xml`
    ];

    const lastmod = new Date().toISOString().split('T')[0];

    let xml = '<?xml version="1.0" encoding="UTF-8"?>\n';
    xml += '<sitemapindex xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n';
    
    sitemaps.forEach(sitemap => {
      xml += '  <sitemap>\n';
      xml += `    <loc>${sitemap}</loc>\n`;
      xml += `    <lastmod>${lastmod}</lastmod>\n`;
      xml += '  </sitemap>\n';
    });
    
    xml += '</sitemapindex>';
    return xml;
  }

  private generateXmlSitemap(urls: SitemapUrl[]): string {
    let xml = '<?xml version="1.0" encoding="UTF-8"?>\n';
    xml += '<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n';
    
    urls.forEach(url => {
      xml += '  <url>\n';
      xml += `    <loc>${url.loc}</loc>\n`;
      
      if (url.lastmod) {
        xml += `    <lastmod>${url.lastmod}</lastmod>\n`;
      }
      
      if (url.changefreq) {
        xml += `    <changefreq>${url.changefreq}</changefreq>\n`;
      }
      
      if (url.priority !== undefined) {
        xml += `    <priority>${url.priority.toFixed(1)}</priority>\n`;
      }
      
      xml += '  </url>\n';
    });
    
    xml += '</urlset>';
    return xml;
  }

  // Generate JSON-LD structured data for different content types
  generateArticleStructuredData(article: {
    title: string;
    description: string;
    author: string;
    publishedTime: string;
    modifiedTime?: string;
    image?: string;
    url: string;
    tags?: string[];
    section?: string;
  }) {
    return {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": article.title,
      "description": article.description,
      "author": {
        "@type": "Person",
        "name": article.author
      },
      "publisher": {
        "@type": "Organization",
        "name": "StrymHub",
        "logo": {
          "@type": "ImageObject",
          "url": `${this.baseUrl}/images/logo.png`
        }
      },
      "datePublished": article.publishedTime,
      "dateModified": article.modifiedTime || article.publishedTime,
      "image": article.image || `${this.baseUrl}/images/default-og.jpg`,
      "url": article.url,
      "mainEntityOfPage": article.url,
      ...(article.tags && { "keywords": article.tags.join(", ") }),
      ...(article.section && { "articleSection": article.section })
    };
  }

  generateVideoStructuredData(video: {
    title: string;
    description: string;
    thumbnailUrl: string;
    videoUrl: string;
    uploadDate: string;
    duration?: string;
    author: string;
  }) {
    return {
      "@context": "https://schema.org",
      "@type": "VideoObject",
      "name": video.title,
      "description": video.description,
      "thumbnailUrl": video.thumbnailUrl,
      "contentUrl": video.videoUrl,
      "uploadDate": video.uploadDate,
      ...(video.duration && { "duration": video.duration }),
      "author": {
        "@type": "Person",
        "name": video.author
      },
      "publisher": {
        "@type": "Organization",
        "name": "StrymHub",
        "logo": {
          "@type": "ImageObject",
          "url": `${this.baseUrl}/images/logo.png`
        }
      }
    };
  }

  generatePodcastStructuredData(podcast: {
    title: string;
    description: string;
    audioUrl: string;
    publishedTime: string;
    duration?: string;
    author: string;
  }) {
    return {
      "@context": "https://schema.org",
      "@type": "AudioObject",
      "name": podcast.title,
      "description": podcast.description,
      "contentUrl": podcast.audioUrl,
      "datePublished": podcast.publishedTime,
      ...(podcast.duration && { "duration": podcast.duration }),
      "author": {
        "@type": "Person",
        "name": podcast.author
      },
      "publisher": {
        "@type": "Organization",
        "name": "StrymHub",
        "logo": {
          "@type": "ImageObject",
          "url": `${this.baseUrl}/images/logo.png`
        }
      }
    };
  }
}

export const sitemapGenerator = new SitemapGenerator();
export default sitemapGenerator;
